import { useState, useEffect } from 'react';
import { Percent, DollarSign, Calendar, Users, Ticket, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { VoucherService, Voucher } from '@/services/voucherService';
import { getPartnerLogoUrl } from '@/config/api';

interface PartnerVouchersProps {
  partnerId: number;
  partnerName?: string;
  partnerAddress?: string;
  partnerPhone?: string;
  partnerEmail?: string;
  partnerLogo?: string;
}

const PartnerVouchers = ({ partnerId, partnerName, partnerAddress, partnerPhone, partnerEmail, partnerLogo }: PartnerVouchersProps) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadVouchers();
  }, [partnerId]);

  const loadVouchers = async () => {
    try {
      setLoading(true);
      const vouchersData = await VoucherService.getVouchersByPartner(partnerId);
      
      // Filtrar apenas vouchers ativos, válidos e não excluídos
      const activeVouchers = vouchersData.filter(voucher => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Resetar horas para comparar apenas datas
        
        // Verificar se está ativo e não foi excluído
        if (!voucher.is_active || voucher.deleted_at) {
          return false;
        }
        
        // Verificar data de início (se informada)
        if (voucher.start_date) {
          const startDate = new Date(voucher.start_date);
          startDate.setHours(0, 0, 0, 0);
          if (today < startDate) {
            return false; // Ainda não começou
          }
        }
        
        // Verificar data de fim (se informada)
        if (voucher.end_date) {
          const endDate = new Date(voucher.end_date);
          endDate.setHours(0, 0, 0, 0);
          if (today > endDate) {
            return false; // Já expirou
          }
        }
        
        // Verificar limite de uso (se informado)
        if (voucher.usage_limit !== null && voucher.usage_count >= voucher.usage_limit) {
          return false; // Limite de uso excedido
        }
        
        return true;
      });
      
      setVouchers(activeVouchers);
    } catch (error) {
      console.error('Erro ao carregar vouchers:', error);
    } finally {
      setLoading(false);
    }
  };


  const printVoucher = async (voucher: Voucher) => {
    // Registrar impressão no banco de dados
    console.log('🖨️ Iniciando impressão do voucher:', voucher.id, voucher.title);
    try {
      await VoucherService.registerPrint(voucher.id);
      console.log('✅ Registro de impressão concluído');
    } catch (error) {
      console.error('❌ Erro ao registrar impressão:', error);
      // Continuar com a impressão mesmo se o registro falhar
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Voucher - ${voucher.title}</title>
        <style>
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
          .voucher { border: 3px solid #000; padding: 20px; margin: 10px 0; page-break-inside: avoid; }
          .header { text-align: center; margin-bottom: 20px; }
          .logo { max-width: 200px; height: auto; margin-bottom: 15px; }
          .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .partner-info { background: #f8f9fa; padding: 15px; margin: 15px 0; border-radius: 8px; border-left: 4px solid #2563eb; }
          .partner-header { display: flex; align-items: center; margin-bottom: 10px; }
          .partner-logo { max-width: 60px; max-height: 60px; margin-right: 15px; border-radius: 8px; }
          .partner-name { font-size: 18px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
          .partner-details { font-size: 14px; color: #666; line-height: 1.4; }
          .code { font-size: 32px; font-weight: bold; letter-spacing: 3px; background: #f0f0f0; padding: 15px; text-align: center; margin: 20px 0; border: 2px dashed #000; }
          .discount { font-size: 20px; font-weight: bold; color: #2563eb; margin: 15px 0; }
          .details { margin: 15px 0; }
          .description { margin: 15px 0; font-style: italic; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
          .validity { font-weight: bold; color: #059669; }
          .instructions { background: #f8f9fa; padding: 15px; border-left: 4px solid #2563eb; margin: 20px 0; }
          .live-stay-brand { text-align: center; margin-top: 15px; font-size: 14px; color: #2563eb; font-weight: bold; }
          
          @media print {
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .voucher { border: 3px solid #000; padding: 20px; margin: 10px 0; page-break-inside: avoid; }
            .header { text-align: center; margin-bottom: 20px; }
            .logo { max-width: 200px; height: auto; margin-bottom: 15px; }
            .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .partner-info { background: #f8f9fa; padding: 15px; margin: 15px 0; border-radius: 8px; border-left: 4px solid #2563eb; }
            .partner-header { display: flex; align-items: center; margin-bottom: 10px; }
            .partner-logo { max-width: 60px; max-height: 60px; margin-right: 15px; border-radius: 8px; }
            .partner-name { font-size: 18px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
            .partner-details { font-size: 14px; color: #666; line-height: 1.4; }
            .code { font-size: 32px; font-weight: bold; letter-spacing: 3px; background: #f0f0f0; padding: 15px; text-align: center; margin: 20px 0; border: 2px dashed #000; }
            .discount { font-size: 20px; font-weight: bold; color: #2563eb; margin: 15px 0; }
            .details { margin: 15px 0; }
            .description { margin: 15px 0; font-style: italic; }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
            .validity { font-weight: bold; color: #059669; }
            .instructions { background: #f8f9fa; padding: 15px; border-left: 4px solid #2563eb; margin: 20px 0; }
            .live-stay-brand { text-align: center; margin-top: 15px; font-size: 14px; color: #2563eb; font-weight: bold; }
          }
        </style>
      </head>
      <body>
        <div class="voucher">
          <div class="header">
            <img src="http://localhost:3000/live-stay-logo.png" alt="LiveStay" class="logo">
            <div class="title">${voucher.title}</div>
            <div class="discount">
              ${voucher.discount_type === 'percentage' ? 'Desconto de ' + voucher.discount_value + '%' : 'Desconto de R$ ' + voucher.discount_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </div>
          
          ${partnerName ? `
          <div class="partner-info">
            <div class="partner-header">
              ${partnerLogo ? `<img src="${getPartnerLogoUrl(partnerLogo)}" alt="${partnerName}" class="partner-logo" onerror="this.style.display='none'">` : ''}
              <div class="partner-name">${partnerName}</div>
            </div>
            <div class="partner-details">
              ${partnerAddress ? `<div><strong>Endereço:</strong> ${partnerAddress}</div>` : ''}
              ${partnerPhone ? `<div><strong>Telefone:</strong> ${partnerPhone}</div>` : ''}
              ${partnerEmail ? `<div><strong>E-mail:</strong> ${partnerEmail}</div>` : ''}
            </div>
          </div>
          ` : ''}
          
          <div class="code">${voucher.code}</div>
          
          <div class="details">
            <div class="validity">
              Válido de ${new Date(voucher.start_date).toLocaleDateString('pt-BR')} até ${new Date(voucher.end_date).toLocaleDateString('pt-BR')}
            </div>
            ${voucher.usage_limit ? `<div>Usos restantes: ${voucher.usage_limit - voucher.usage_count}</div>` : ''}
          </div>
          
          ${voucher.description ? `<div class="description">${voucher.description}</div>` : ''}
          
          <div class="instructions">
            <strong>Como usar:</strong><br>
            Apresente este voucher impresso ou digital no estabelecimento para receber seu desconto.
          </div>
          
          <div class="live-stay-brand">
            Powered by LiveStay
          </div>
          
          <div class="footer">
            Voucher gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Aguarda o carregamento da página antes de tentar imprimir
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
    
    // Fecha a janela após a impressão (ou cancelamento)
    printWindow.onafterprint = () => {
      printWindow.close();
    };
  };

  const formatDiscount = (voucher: Voucher) => {
    if (voucher.discount_type === 'percentage') {
      return `${voucher.discount_value}%`;
    } else {
      return `R$ ${voucher.discount_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    }
  };

  const getDiscountIcon = (voucher: Voucher) => {
    return voucher.discount_type === 'percentage' ? (
      <Percent className="h-5 w-5" />
    ) : (
      <DollarSign className="h-5 w-5" />
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-primary" />
            Vouchers Disponíveis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (vouchers.length === 0) {
    return null; // Não exibe nada se não houver vouchers ativos
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-primary" />
          Vouchers Disponíveis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {vouchers.map((voucher) => (
            <div key={voucher.id} className="border rounded-lg p-4 bg-gradient-to-r from-primary/5 to-primary/10">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg">{voucher.title}</h3>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                      Ativo
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1 font-medium">
                      {getDiscountIcon(voucher)}
                      Desconto de {formatDiscount(voucher)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Válido até {new Date(voucher.end_date).toLocaleDateString('pt-BR')}
                    </span>
                    {voucher.usage_limit && (
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {voucher.usage_limit - voucher.usage_count} restantes
                      </span>
                    )}
                  </div>
                  
                  {voucher.description && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {voucher.description}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <code className="bg-background px-3 py-2 rounded-lg text-lg font-mono font-bold border-2 border-primary/20">
                      {voucher.code}
                    </code>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => printVoucher(voucher)}
                      className="gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      Imprimir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            💡 <strong>Como usar:</strong> Clique em "Imprimir" para gerar um voucher impresso e apresente no estabelecimento.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PartnerVouchers;
